import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { partial, debounce } from 'underscore';
import moment from 'moment';
import { animateScroll } from 'react-scroll';
import { formatPrice } from 'utils/common';
import i18n from 'utils/translate';
import NavigationButtons from 'components/NavigationButtons';
import RadioButton from 'components/RadioButton';
import Section from 'components/Section';
import TextField from 'components/TextField';
import { DATE_ERROR_MESSAGES, EVENT_TYPE_SMALL, EVENT_TYPE_LARGE, CALC_API_DEBOUNCE_DURATION } from '../../constants';
import { toast } from 'react-toastify';
import InfoTextHeader from 'components/InfoTextHeader';
import './styles.scss';
import ErrorMessage from 'components/ErrorMessage';

const linkTypeShape = {
  url: PropTypes.string,
  go: PropTypes.func.isRequired
};

const propTypes = {
  calculateQuote: PropTypes.func.isRequired,
  backLink: PropTypes.shape(linkTypeShape),
  nextLink: PropTypes.shape(linkTypeShape).isRequired,
  onChange: PropTypes.func.isRequired
};

const defaultProps = { onChange: console.log };

class EventDetails extends Component {
  constructor(props) {
    super(props);

    this.state = {
      veranstaltungsArt: EVENT_TYPE_SMALL,
      veranstaltungsDatum: moment().add(1, 'day'),
      infoAccept: false,
      ...props.data,
      submitted: false,
      validForm: false,
      options: [],
      wrongDate: null
    };
  }

  componentDidMount() {
    this.calcQuote();
    animateScroll.scrollToTop({ duration: 0 });
  }

  handleReverse = e => {
    const {
      backLink: { go } = {
        go: () => {}
      }
    } = this.props;
    e.preventDefault();
    e.stopPropagation();
    go();
  };

  handleSubmit = e => {
    const {
      nextLink: { go } = {
        go: () => {}
      },
      onChange = () => {}
    } = this.props;
    const form = this.refs.form;

    if (form.checkValidity && !form.checkValidity()) {
      form.reportValidity();
      return;
    }

    onChange(this.state);
    go();

    window.dataLayer.push({
      event: 'onlineabschluss',
      formname: 'Maturaball-Versicherung',
      action: 'Schritt 1 von 4'
    });
  };

  calcQuote() {
    const { veranstaltungsArt } = this.state;

    if (veranstaltungsArt !== undefined) {
      this.requestQuote();
    }
  }

  requestQuote = debounce(() => {
    const { calculateQuote } = this.props;

    calculateQuote({
      ...this.state
    }).then(quote => {
      this.setState({ quote });
    });
  }, CALC_API_DEBOUNCE_DURATION);

  handleDataChange = (data, key) => {
    const { submitted } = this.state;
    const { onChange } = this.props;

    this.setState(
      {
        [`${key}`]: data
      },
      () => {
        this.calcQuote();
        onChange(this.state);
        if (submitted) {
          this.setState({ validForm: this.refs.form.checkValidity() });
        }
      }
    );
  };

  handleEventTypeCheck = async (type, e) => {
    if (e.target.checked) {
      await this.setState({ veranstaltungsArt: type });
      this.calcQuote();
    }
  };

  handleInfoAccept = ({ target: { checked: value } }) => {
    const { submitted } = this.state;

    this.setState({ infoAccept: value });
    if (submitted) {
      this.setState({ validForm: this.refs.form.checkValidity() });
    }
  };

  render() {
    const { backLink } = this.props;
    const {
      quote,
      veranstaltungsArt,
      veranstaltungsDatum,
      veranstaltungsOrt,
      schuleUndKlasse,
      submitted,
      wrongDate,
      downloadedBasicInfo,
      infoAccept
    } = this.state;
    const basicInfoDownloadClass = submitted && !downloadedBasicInfo ? 'download-error' : '';
    const basicInfoLink = window.documentLinks ? window.documentLinks.basicInfos : '';
    let finalPrice = null;
    let currency = null;
    let customError = '';
    let minDate = moment();

    if (quote) {
      finalPrice = quote.price;
      currency = quote.currency;
    }

    if (moment.isMoment(wrongDate) && wrongDate.isValid() && wrongDate.isBefore(minDate)) {
      if (wrongDate.isBefore(moment())) {
        customError = DATE_ERROR_MESSAGES.inFuture;
      }
    }

    return (
      <form
        ref="form"
        onSubmit={this.handleSubmit}
        className={`page-step-wrapper event-details page-step-wrapper--narrow ${submitted ? 'submitted' : ''}`}
        id="insurance-form"
      >
        <Section header="Information zum Produktvertreiber">
          <InfoTextHeader checked={infoAccept} onChange={value => this.handleInfoAccept(value)} />
        </Section>
        <Section header="Art der Veranstaltung">
          <div className="event-type-wrapper">
            <RadioButton
              className="radiobutton-wrapper col-sm-12 col-md-6"
              id="eventSmall"
              text={i18n('Maturaball mit max. 1.000 Teilnehmern und Gästen')}
              checked={veranstaltungsArt === EVENT_TYPE_SMALL}
              onChange={partial(this.handleEventTypeCheck, EVENT_TYPE_SMALL)}
            />
            <RadioButton
              className="radiobutton-wrapper col-sm-12 col-md-6"
              id="eventLarge"
              text={i18n('Maturaball mit max. 5.000 Teilnehmern und Gästen')}
              checked={veranstaltungsArt === EVENT_TYPE_LARGE}
              onChange={partial(this.handleEventTypeCheck, EVENT_TYPE_LARGE)}
            />
          </div>
        </Section>
        <Section header="Versicherungssumme">
          <div className="event-type-wrapper">
            <RadioButton
              className="radiobutton-wrapper col-xs-12"
              id="sum"
              text={i18n('€ 2.000.000,- für Personen- und Sachschäden')}
              questionMark={i18n(
                'Höhere Summen können online nicht abgeschlossen werden. Bitte wenden Sie sich in diesem Fall an die Abteilung VH-UHR unter 0316/8037-6228 oder center.gd.vuhr@grawe.at.'
              )}
              checked={true}
              onChange={() => false}
            />
          </div>
        </Section>
        <Section header={i18n('Details zur Veranstaltung')}>
          <TextField
            label={i18n('Datum der Veranstaltung')}
            name="veranstaltungsDatum"
            className="col-xs-12 col-md-4"
            required
            value={veranstaltungsDatum}
            minDate={minDate}
            date
            placeholder={i18n('TT.MM.JJJJ')}
            onChange={data => this.handleDataChange(data, 'veranstaltungsDatum')}
            onWrongDate={wrongDate => this.setState({ wrongDate })}
            customError={customError}
            ref="date"
          />

          <TextField
            label={i18n('Veranstaltungsort')}
            name="location"
            className="col-xs-12 col-md-4"
            value={veranstaltungsOrt}
            required
            onChange={data => this.handleDataChange(data, 'veranstaltungsOrt')}
            ref="location"
          />

          <TextField
            label={i18n('Schule und Klasse')}
            name="schoolClass"
            className="col-xs-12 col-md-4"
            value={schuleUndKlasse}
            required
            onChange={data => this.handleDataChange(data, 'schuleUndKlasse')}
            ref="schoolClass"
          />
        </Section>

        <div className="row">
          {veranstaltungsDatum && (
            <div className="col-xs-12 hint">
              {i18n(
                `Wir empfehlen Ihnen aufgrund Ihrer Angaben unsere Veranstalter-Haftpflichtversicherung für Ihren Maturaball am ${veranstaltungsDatum.format(
                  'DD.MM.YYYY'
                )}.`
              )}
            </div>
          )}
        </div>

        <div className="row">
          <div className="bib-wrapper col-xs-12">
            <p>
              {i18n(
                'Die standardisierten Produkt- bzw. Basisinformationsblätter beinhalten wichtige Informationen zur Veranstalter-Haftpflichtversicherung für Maturabälle. Bitte nehmen Sie sich ausreichend Zeit und lesen Sie diese sorgfältig.'
              )}
            </p>
            <a
              className="col-xs-12 basicInfos-pdf"
              href={basicInfoLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => this.handleDataChange(true, 'downloadedBasicInfo')}
            >
              <span className="pdf-icon col-xs-2" />
              <span className={`col-xs-10 ${basicInfoDownloadClass}`}>
                {i18n('Download der standardisierten Produkt- bzw. Basisinformationsblätter')}
                <span className="field-error-message">
                  {i18n(
                    'Die standardisierten Produkt- bzw. Basisinformationsblätter sind verpflichtend herunterzuladen, wenn Sie überlegen, die Maturaball-Versicherung online abzuschließen.'
                  )}
                </span>
              </span>
            </a>
            <p className="p-margin-top">
              {i18n(
                'Sie können dieses Dokument sowie sämtliche vorvertragliche Informationen auch in Papierform erhalten.'
              )}
              <br />
              {i18n('Bitte senden Sie dazu ein Mail mit Ihrer Postanschrift an ')}
              <a href="mailto:center.gd.vuhr@grawe.at.?subject=Postversand Vorvertragliche Informationen">
                center.gd.vuhr@grawe.at
              </a>
              .
              <br />
              {i18n('Nach Erhalt der Dokumente können Sie mit dem Online-Vertragsabschluss fortfahren.')}
            </p>
          </div>
        </div>
        <div className="sticky-footer">
          <div className="page-step-wrapper__price">
            {i18n('Prämie (einmalig)')}&nbsp;
            {formatPrice(finalPrice, currency)}
          </div>
          <NavigationButtons
            nextLink={{
              go: e => {
                this.setState({
                  submitted: true,
                  validForm: this.refs.form.checkValidity && this.refs.form.checkValidity()
                });

                Object.keys(this.refs).forEach(key => {
                  if (this.refs[key].onBlur) {
                    this.refs[key].onBlur();
                  }
                });

                if (
                  quote &&
                  quote.price &&
                  downloadedBasicInfo &&
                  this.refs.form.checkValidity &&
                  this.refs.form.checkValidity()
                ) {
                  this.handleSubmit();
                  toast.dismiss();
                } else {
                  toast.error(<ErrorMessage message={i18n('Bitte überprüfen Sie Ihre Angaben')} />);
                }
              }
            }}
            backLink={backLink}
          />
        </div>
      </form>
    );
  }
}

EventDetails.defaultProps = defaultProps;
EventDetails.propTypes = propTypes;

export default EventDetails;
